import React, { useState } from "react";
import "./footer.css";
import { Link } from "react-router-dom";

import {
  FaMapMarkerAlt,
  FaEnvelope,
  FaGlobe,
  FaPhoneAlt,
} from "react-icons/fa";

const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

function Form() {
  /* ── Contact form state ─────────────────────────── */
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    countryCode: "+91",
    phone: "",
    exploringFor: "",
    hearAbout: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const validate = () => {
    const errors = {};

    if (!formData.fullName.trim()) {
      errors.fullName = "Full name is required";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    }

    if (!formData.exploringFor) {
      errors.exploringFor = "Please select an option";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setLoading(true);
    setApiError("");

    try {
      const res = await fetch(`${BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.fullName,
          phone: `${formData.countryCode}${formData.phone}`,
          email: formData.email || null,
          subject: `Exploring for: ${formData.exploringFor}`,
          message: `Heard about us via: ${
            formData.hearAbout || "Not specified"
          }. Phone: ${formData.countryCode}${formData.phone}`,
          type: "message",
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Submission failed");
      }

      setSubmitted(true);

      setFormData({
        fullName: "",
        email: "",
        countryCode: "+91",
        phone: "",
        exploringFor: "",
        hearAbout: "",
      });
    } catch (err) {
      setApiError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="contact-section">
        <div className="contact-container">

          {/* LEFT — Form */}
          <div className="contact-form">
            <h2>Connect with us</h2>

            {submitted ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "30px 0",
                }}
              >
                <div
                  style={{
                    fontSize: 48,
                    marginBottom: 12,
                  }}
                >
                  ✅
                </div>

                <h3
                  style={{
                    color: "var(--base-primary)",
                    marginBottom: 8,
                  }}
                >
                  Thank you!
                </h3>

                <p
                  style={{
                    color: "var(--base-neutral)",
                    fontSize: 14,
                    lineHeight: 1.7,
                  }}
                >
                  We've received your enquiry and will get back to you shortly.
                </p>

                <button
                  type="button"
                  className="submit-btn"
                  style={{ marginTop: 16 }}
                  onClick={() => {
                    setSubmitted(false);
                    setApiError("");
                    setErrors({});
                  }}
                >
                  Send Another →
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>

                {/* API Error */}
                {apiError && (
                  <div
                    style={{
                      background: "#FEF2F2",
                      border: "1px solid #FECACA",
                      borderRadius: 8,
                      padding: "10px 14px",
                      marginBottom: 16,
                      color: "#DC2626",
                      fontSize: 13,
                    }}
                  >
                    ⚠️ {apiError}
                  </div>
                )}

                {/* Full Name */}
                <div className="form-group">
                  <label htmlFor="fullName">Full Name *</label>

                  <input
                    id="fullName"
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    autoComplete="name"
                  />

                  {errors.fullName && (
                    <span className="error">{errors.fullName}</span>
                  )}
                </div>

                {/* Email */}
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>

                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    autoComplete="email"
                  />
                </div>

                {/* Phone */}
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>

                  <div className="phone-row">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      aria-label="Country code"
                    >
                      <option value="+91">🇮🇳 +91</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+61">🇦🇺 +61</option>
                      <option value="+971">🇦🇪 +971</option>
                    </select>

                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone number"
                      autoComplete="tel"
                      inputMode="numeric"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/\D/g, "");
                      }}
                    />
                  </div>

                  {errors.phone && (
                    <span className="error">{errors.phone}</span>
                  )}
                </div>

                {/* Exploring For */}
                <div className="form-group">
                  <label htmlFor="exploringFor">
                    Exploring Suvidha for *
                  </label>

                  <select
                    id="exploringFor"
                    name="exploringFor"
                    value={formData.exploringFor}
                    onChange={handleChange}
                  >
                    <option value="">Select option</option>
                    <option value="Parents">My Parents</option>
                    <option value="Self">Myself</option>
                    <option value="Spouse">My Spouse</option>
                    <option value="Relatives">My Relatives</option>
                    <option value="Others">Others</option>
                  </select>

                  {errors.exploringFor && (
                    <span className="error">
                      {errors.exploringFor}
                    </span>
                  )}
                </div>

                {/* How Did You Hear */}
                <div className="form-group">
                  <label htmlFor="hearAbout">
                    How did you hear about us?
                  </label>

                  <select
                    id="hearAbout"
                    name="hearAbout"
                    value={formData.hearAbout}
                    onChange={handleChange}
                  >
                    <option value="">Select option</option>
                    <option value="Google Search">Google Search</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="YouTube">YouTube</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Friend or Family">
                      Friend or Family
                    </option>
                    <option value="Doctor Referral">
                      Doctor Referral
                    </option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Submit →"}
                </button>
              </form>
            )}
          </div>

          {/* RIGHT — Contact Info */}
          <div className="contact-info">
            <h2
              style={{
                marginBottom: 28,
                fontSize: 26,
                fontWeight: 800,
              }}
            >
              Suvidha Elder Care
            </h2>

            <p
              style={{
                marginBottom: 28,
                lineHeight: 1.8,
                opacity: 0.9,
              }}
            >
              Compassionate elder care services designed to give your loved
              ones the support, safety and dignity they deserve — right at
              home.
            </p>

            <div className="info-item">
              <FaMapMarkerAlt className="icon" />

              <p>
                Address: H/1 Plot no11, Hno 6-3-347/11, 
                <br />
                Dwarakapuri, Punjagutta, Hyderabad, Telangana 500082
              </p>
            </div>

            <div className="info-item">
              <FaPhoneAlt className="icon" />

              <p>+91 9876543210</p>
            </div>

            <div className="info-item">
              <FaEnvelope className="icon" />

              <p>support@suvidhaeldercare.com</p>
            </div>

            <div className="info-item">
              <FaGlobe className="icon" />

              <p >
                <Link style={{ display: "inline-block", color: "var(--white)", textDecoration: "none" }} to = "/">www.suvidhaeldercare.com</Link>
              </p>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

export default Form;
